/**
 * @voltxampere/engine — Formula detector shared helpers (Sprint 56b)
 *
 * Subgraph matching primitives reused by every template — net
 * adjacency, parameter extraction, ground / source lookup, LaTeX
 * value formatting.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
/**
 * Coerce a ParamValue to a finite number. ParamValue is a union of
 * `number | string | boolean | { value, unit }`; the detector only
 * deals with the numeric forms (resistance, capacitance, voltage,
 * frequency). Returns null when the value is missing or non-numeric.
 */
export function asNumber(value) {
    if (typeof value === "number" && Number.isFinite(value))
        return value;
    if (typeof value === "object" && value !== null && "value" in value) {
        const inner = value.value;
        if (typeof inner === "number" && Number.isFinite(inner))
            return inner;
    }
    return null;
}
/** Return the netId of the named terminal on `component`, or null. */
export function netOf(component, terminalId) {
    return component.terminals.find((t) => t.terminalId === terminalId)?.netId ?? null;
}
/** Return the GND net id, or null when the circuit has none. */
export function findGroundNetId(circuit) {
    return circuit.nets.find((n) => n.isGround)?.id ?? null;
}
/**
 * Locate a DC voltage source that drives `netId` (positive terminal).
 * Returns null when no source is attached. Voltage value is whatever
 * `params.voltage` stores; the caller calls `asNumber` to coerce.
 */
export function findVoltageSourceDriving(circuit, netId) {
    for (const c of circuit.components) {
        if (c.type !== "voltage-source" && c.type !== "dc-source")
            continue;
        const posNet = netOf(c, "pos") ?? netOf(c, "p");
        if (posNet === netId)
            return c;
    }
    return null;
}
/**
 * Build a net → component terminal index. Used to walk shared nets
 * (find the second resistor on a divider tap, find the op-amp pin
 * attached to the feedback net, …).
 */
export function buildNetIndex(circuit) {
    const idx = new Map();
    for (const c of circuit.components) {
        for (const t of c.terminals) {
            let bucket = idx.get(t.netId);
            if (bucket === undefined) {
                bucket = [];
                idx.set(t.netId, bucket);
            }
            bucket.push({ component: c, terminalId: t.terminalId });
        }
    }
    return idx;
}
/**
 * Filter components by type, scoped by an optional selection set.
 * When `selected` is undefined or empty, returns every component of
 * the requested type. Otherwise filters down to the selected ids.
 */
export function componentsOfType(circuit, type, selected) {
    const all = circuit.components.filter((c) => c.type === type);
    if (selected === undefined || selected.size === 0)
        return all;
    return all.filter((c) => selected.has(c.id));
}
/**
 * S56b — op-amp / comparator type aliases. The component registry
 * canonicalised on "op-amp" (Sprint 14 ADR-0020) but downstream
 * presets ship variants written as "opamp" (fathers preset Sprint
 * 54.2a, etc.). The detector recognises both so real-world fixtures
 * surface their natural pattern matches.
 */
export const OPAMP_TYPE_ALIASES = new Set(["op-amp", "opamp"]);
export function isOpampType(type) {
    return OPAMP_TYPE_ALIASES.has(type);
}
/** Filter components by op-amp aliases. */
export function opampsInCircuit(circuit, selected) {
    const all = circuit.components.filter((c) => isOpampType(c.type));
    if (selected === undefined || selected.size === 0)
        return all;
    return all.filter((c) => selected.has(c.id));
}
/**
 * Resolve an op-amp terminal across canonical + alias names.
 * Sprint 14 canonical: "inverting" / "non-inverting" / "output".
 * Fathers / preset aliases: "in-" / "in+" / "out".
 * Sprint 54.2a behavioural opamp: "in_neg" / "in_pos" / "out".
 */
export function opampTerminal(component, kind) {
    const ALIAS_MAP = {
        inverting: ["inverting", "in-", "in_neg"],
        "non-inverting": ["non-inverting", "in+", "in_pos"],
        output: ["output", "out"],
    };
    for (const name of ALIAS_MAP[kind]) {
        const found = netOf(component, name);
        if (found !== null)
            return found;
    }
    return null;
}
/** Quick check whether `netId` is the ground net. */
export function isGroundNet(circuit, netId) {
    if (netId === null)
        return false;
    return findGroundNetId(circuit) === netId;
}
/**
 * Format a numerical value with an engineering-style SI prefix so the
 * substituted LaTeX reads like "1\,\text{k}\Omega" instead of "1000\,
 * \Omega". Optionally pass a unit suffix the caller wants appended
 * (e.g. "Hz", "V", "F").
 */
export function formatLatexValue(value, unit) {
    if (!Number.isFinite(value) || value === 0)
        return `0\\,\\text{${unit}}`;
    const abs = Math.abs(value);
    const log = Math.floor(Math.log10(abs) / 3);
    const PREFIXES = {
        [-4]: "p",
        [-3]: "n",
        [-2]: "\\mu ",
        [-1]: "m",
        [0]: "",
        [1]: "k",
        [2]: "M",
        [3]: "G",
    };
    const prefix = PREFIXES[log] ?? "";
    const scaled = value / 10 ** (log * 3);
    const display = Number.isInteger(scaled)
        ? scaled.toFixed(0)
        : scaled.toPrecision(3).replace(/\.?0+$/, "");
    return `${display}\\,\\text{${prefix}${unit}}`;
}
/**
 * Best-match resolver — given multiple {@link MatchResult}s on the
 * same circuit, pick the one with the highest specificity. Ties
 * break in registration order so the result is deterministic.
 */
export function pickBestMatch(matches) {
    if (matches.length === 0)
        return null;
    const order = { low: 0, medium: 1, high: 2 };
    let best = matches[0];
    if (best === undefined)
        return null;
    for (const m of matches.slice(1)) {
        if (order[m.specificity] > order[best.specificity])
            best = m;
    }
    return best;
}
//# sourceMappingURL=helpers.js.map